import { useRouter } from "next/router";
import { useCurrentUser } from "queries/current-user";
import React, { useMemo } from "react";
import useRequest from "./useRequest";

// The raw team list behind `GET /bootcamps`, always an array. A 2xx body is not
// proof of one: an intercepting proxy or captive portal answers with an HTML
// page and a 200, and axios hands that back as a string. Memoized on the SWR
// payload so the reference is stable enough to use as an effect dependency.
export const useBootcampList = (options = {}) => {
  const { data, ...request } = useRequest("bootcamps", options);
  const bootcamps = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  return { ...request, bootcamps };
};

export const useBootcamps = (props = {}) => {
  const {
    query: { bootcampId },
  } = useRouter();
  const { currentUser, isCoach } = useCurrentUser();
  const { bootcamps: list, revalidate } = useBootcampList({
    ...props,
    refetchOnWindowFocus: false,
    // The team list rarely changes mid-session, but this hook mounts once
    // per movement/section row in the workout builder (via the audio
    // pipeline) — the default 5s dedupe let every playlist click past that
    // window refire GET /bootcamps.
    dedupingInterval: 60_000,
  });

  const bootcamps = useMemo(() => {
    if (list.length === 1) return list;
    if (!isCoach) return list;
    if (!currentUser?.id) return list;

    // A coach whose bootcampId names no team in the list (unset, or a team
    // they no longer have) has nothing to pull to the front. `find` yields
    // undefined there, and a hole in this array throws in every `({ id })`
    // destructure below.
    const coachTeam = list.find(({ id }) => currentUser.bootcampId === id);
    if (!coachTeam) return list;

    const filtered = list.filter(({ id }) => currentUser.bootcampId !== id);
    return [coachTeam, ...filtered];
  }, [list, currentUser, isCoach]);

  const [currentBootcamp] = React.useMemo(
    () =>
      bootcampId ? bootcamps.filter(({ id }) => bootcampId === id) : bootcamps,
    [bootcampId, bootcamps]
  );

  const getBootcampById = React.useCallback(
    (teamID) => {
      if (teamID) {
        return bootcamps.filter(({ id }) => teamID === id)?.[0];
      }
    },
    [bootcamps]
  );

  return {
    revalidate,
    bootcamps,
    bootcampId,
    currentBootcamp,
    getBootcampById,
  };
};
